import Alert from '@mui/material/Alert';
import { BigNumber, utils } from 'ethers';
import React, { useState, useContext, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useAccount, useBlockNumber } from 'wagmi';
import { ContractContext } from '../../../context-providers/ContractsProvider';
import { ServicesContext } from '../../../context-providers/ServicesProvider';
import { contractAddresses, tokenDecimals } from '../../../helpers/config/contracts.config';
import { hardcodedErrorCodes } from '../../../helpers/error-message.helper';
import { lockBalanceStore } from '../../../stores/LockBalanceStore';
import AmountInput from '../../AmountInput/AmountInput';
import NereusButton from '../../NereusButton/NereusButton';
import StatusIcon from '../../StatusIcon/StatusIcon';
import ModalWrapper from '../ModalWrapper/ModalWrapper';
import './LockDialog.scss';
import {balanceStore} from "../../../stores/BalanceStore";

const buttonTexts = {
    approveAndLock: 'Approve and Lock NRS',
    enterAmount: 'Enter Amount',
    insufficientFunds: 'Insufficient Funds',
    approving: 'Approving NRS',
    deposit: 'Lock',
    depositing: 'Locking',
    retryApproveAndLock: 'Retry Approve and Lock NRS',
    retryLock: 'Retry Lock NRS'
};

const LockDialog: React.FC = observer(() => {
    const { data: blockNumber } = useBlockNumber({ watch: true });
    const { address: userAddress } = useAccount();
    const { depositWithdrawLockService } =  useContext(ServicesContext);
    const [amount, setAmount] = useState<string>('0');
    const [error, setError] = useState<string | null>(null);
    const [btnDisabled, setBtnDisabled] = useState<boolean>(true);
    const [approvedAmount, setApprovedAmount] = useState<BigNumber>(BigNumber.from('0'));
    const [buttonText, setButtonText] = useState<string>(buttonTexts.enterAmount);
    const [isPending, setIsPending] = useState<boolean>(false);
    const [isRetry, setIsRetry] = useState<boolean>(false);
    const { nrsContract } = useContext(ContractContext);

    useEffect(() => {
        const getAndSetApprovedAmount = async () => {
            const allowed = await nrsContract.read.allowance([userAddress, contractAddresses.base.lockNrs]);

            setApprovedAmount(BigNumber.from(allowed));
        }
        if (userAddress && blockNumber && nrsContract) {
            getAndSetApprovedAmount();
        } else {
            setApprovedAmount(BigNumber.from('0'));
        }

    }, [userAddress, blockNumber, nrsContract]);

    useEffect(() => {
        if (!amount || Number(amount) === 0) {
            return setButtonText(buttonTexts.enterAmount);
        } else {
            if (error) {
                if (error === hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE) {
                    return setButtonText(buttonTexts.insufficientFunds);
                }
            }
            if (approvedAmount.gte(utils.parseUnits(amount, tokenDecimals.nrs))) {
                if (isPending) {
                    return setButtonText(buttonTexts.depositing);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryLock : buttonTexts.deposit);
                }

            } else {
                if (isPending) {
                    return setButtonText(buttonTexts.approving);
                } else {
                    return setButtonText(isRetry ? buttonTexts.retryApproveAndLock : buttonTexts.approveAndLock);
                }
            }
        }

    }, [amount, error, approvedAmount, isRetry, isPending]);

    useEffect(() => {
        if ((amount && Number(amount) > 0) && (error !== hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE)) {
            setBtnDisabled(false);
        } else {
            setBtnDisabled(true);
        }
    }, [amount, error]);

    useEffect(() => {
        if (amount && Number(amount) > Number(lockBalanceStore.nrsTokenBalanceDecimal)) {
            setError(hardcodedErrorCodes.FE_NOT_ENOUGH_WALLET_BALANCE);
        } else {
            setError(null);
        }
    }, [amount, lockBalanceStore.nrsTokenBalanceDecimal]);

    const handleApproveAndLock = async () => {
        setIsPending(true);
        setIsRetry(false);
        if (approvedAmount.lt(utils.parseUnits(amount, tokenDecimals.nrs))) {
            try {
                setButtonText(buttonTexts.approving);
                await depositWithdrawLockService.approveAmount(utils.parseUnits(amount, tokenDecimals.nrs));
                await new Promise(resolve => {
                    setTimeout(resolve, 3000)
                })
            } catch (err) {
                setIsPending(false);
                setIsRetry(true);
                return;
            }
        }

        try {
            setButtonText(buttonTexts.depositing);
            await depositWithdrawLockService.deposit(utils.parseUnits(amount, tokenDecimals.nrs));
            if (userAddress) {
                balanceStore.fetchBalance(userAddress)
            }
        } catch (err) {
            setButtonText(buttonTexts.retryLock);
            setIsPending(false);
            setIsRetry(true);
            return;
        }
        setIsPending(false);
    };

    return (
        <ModalWrapper className="LockDialog"
            headerChildren={<h2 className="nereus-body-m">Lock</h2>}
            bodyChildren={<div className="LockDialog__body">
                <div>
                    <AmountInput label="Amount" asset="NRS" hasMaxButton={true} value={amount}
                                 available={lockBalanceStore.nrsTokenBalanceDecimal}
                                 onChange={amount => setAmount(amount + '')}/>
                    {error && <Alert className="LockDialog__error mt-4" variant="standard" severity="error">
                        <span className="nereus-caption">{error}</span>
                    </Alert>}
                    <Alert className="LockDialog__error mt-4" variant="standard" severity="info">
                        <span className="nereus-caption"><span className="nereus-color-main">Lock NRS with a 90 days withdrawal delay.</span> After the 90 days lock-up, your tokens become available for withdrawal without restrictions. Ideal for those who believe in the project’s future and want to contribute to its long-term health.</span>
                    </Alert>
                </div>
                <div className="flex pb-4 mt-4">
                    <NereusButton disabled={btnDisabled || isPending} variant="contained"
                                  onClick={handleApproveAndLock} className="bg-light-green text-dark-blue w-full">
                        {buttonText} {isPending && <StatusIcon className="ml-1 inline" status="disabled-pending"/>}
                    </NereusButton>
                </div>
            </div>}
        />
    );
});

export default LockDialog;
